// ============================================================================
// The Assistant asks the QuoteMaker bar to act (the bar command bridge).
// ----------------------------------------------------------------------------
// The QuoteMaker bar (components/quote-brain/QuoteBrainBar.tsx) owns a run's
// life in the tab: it saves the job before a start or an Adopt, follows the
// run it started, marks the job live so Stage 4, Stage 5 and the Assistant
// leave the items alone, and loads the version QuoteMaker wrote. An Assistant
// action that called the routes itself would skip all of that, so the
// Assistant's start, stop, Adopt, Keep mine and writer changes go through the
// bar instead: one window event, answered by the bar on screen for that job
// with a promise of what happened. With no bar for the job (QuoteMaker is not
// on screen) the request is answered at once, plainly, and nothing changes.
// ============================================================================

export const QUOTEMAKER_WRITER_COMMAND_EVENT = 'qm-quotemaker-writer-command';

export type QuoteMakerWriterAction =
    | 'start' // Write the quote, Write again, or Continue / Try again on a paused or failed run
    | 'stop'
    | 'adopt'
    | 'keep_mine'
    | 'write_by_hand' // the builder writes the job: before QuoteMaker starts, or after a run that could not finish
    | 'hand_back'; // the builder hands a job written by hand back to QuoteMaker

export interface QuoteMakerWriterOutcome {
    ok: boolean;
    message: string;
}

export interface QuoteMakerWriterCommandDetail {
    projectId: string;
    action: QuoteMakerWriterAction;
    /** The run the request was made about, when it names one (Adopt and Keep mine). */
    runId?: string | null;
    /** Set by the bar that takes the request. */
    completion: Promise<QuoteMakerWriterOutcome> | null;
}

export const NO_QUOTEMAKER_BAR_MESSAGE = 'Nothing changed: this job is not open in QuoteMaker on this tab. Open the job, then ask again.';

/** Ask the bar showing this job to act. Resolves with what happened; never throws. */
export function requestQuoteMakerWriterAction(
    projectId: string,
    action: QuoteMakerWriterAction,
    { runId = null }: { runId?: string | null } = {},
): Promise<QuoteMakerWriterOutcome> {
    if (typeof window === 'undefined' || !projectId) return Promise.resolve({ ok: false, message: NO_QUOTEMAKER_BAR_MESSAGE });
    const detail: QuoteMakerWriterCommandDetail = { projectId: String(projectId), action, runId, completion: null };
    try {
        window.dispatchEvent(new CustomEvent(QUOTEMAKER_WRITER_COMMAND_EVENT, { detail }));
    } catch {
        return Promise.resolve({ ok: false, message: NO_QUOTEMAKER_BAR_MESSAGE });
    }
    if (!detail.completion) return Promise.resolve({ ok: false, message: NO_QUOTEMAKER_BAR_MESSAGE });
    return Promise.resolve(detail.completion).catch((err: any) => ({ ok: false, message: String(err?.message || 'QuoteMaker could not do that.') }));
}
